import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  message, 
  Row, 
  Col, 
  Statistic,
  Space,
  Tag
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  LockOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { updateUser } from '../store/slices/authSlice';
import axios from 'axios';

function Profile() {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const [userStats, setUserStats] = useState({
    totalRequests: 0,
    completedRequests: 0,
    pendingRequests: 0,
    totalCharsUsed: 0
  });

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await fetchUserStats();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [user]);

  const fetchUserStats = async () => {
    try {
      const response = await axios.get('/api/text-to-voice/');
      
      // Handle paginated response
      const requests = response.data?.data || response.data || [];
      
      setUserStats({
        totalRequests: requests.length,
        completedRequests: requests.filter(r => r.status === 'completed').length,
        pendingRequests: requests.filter(r => r.status === 'pending').length,
        totalCharsUsed: requests.reduce((sum, r) => sum + (r.chars || 0), 0)
      });
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
    }
  };

  const handleEdit = () => {
    setEditing(true);
    // Form will use initialValues automatically
  };

  const handleCancel = () => {
    setEditing(false);
    form.resetFields();
    // Reset to initial values
    form.setFieldsValue({
      username: user?.username || '',
      email: user?.email || ''
    });
  };

  const handleSave = async (values) => {
    try {
      setLoading(true);
      await axios.put('/api/profile/', values);
      
      // Update user in Redux store
      dispatch(updateUser({
        username: values.username,
        email: values.email
      }));
      
      message.success('Profile updated successfully');
      setEditing(false);
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (values) => {
    try {
      setPasswordLoading(true);
      await axios.put('/api/profile/change-password', {
        current_password: values.current_password,
        new_password: values.new_password
      });
      
      message.success('Password changed successfully');
      // Reset form after successful change
      form.resetFields(['current_password', 'new_password', 'confirm_password']);
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Profile</h1>
        <p>Manage your account information and settings</p>
      </div>

      <Row gutter={[24, 24]}>
        {/* Profile Information */}
        <Col xs={24} lg={16}>
          <Card 
            title="Profile Information" 
            extra={
              !editing ? (
                <Button 
                  type="primary" 
                  icon={<EditOutlined />} 
                  onClick={handleEdit}
                >
                  Edit Profile
                </Button>
              ) : (
                <Space>
                  <Button 
                    icon={<CloseOutlined />} 
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<SaveOutlined />} 
                    onClick={() => form.submit()}
                    loading={loading}
                  >
                    Save
                  </Button>
                </Space>
              )
            }
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              disabled={!editing}
              initialValues={{
                username: user?.username || '',
                email: user?.email || ''
              }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="username"
                    label="Username"
                    rules={[
                      { required: true, message: 'Please input username!' },
                      { min: 3, message: 'Username must be at least 3 characters!' }
                    ]}
                  >
                    <Input 
                      prefix={<UserOutlined />} 
                      placeholder="Username"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: true, message: 'Please input email!' },
                      { type: 'email', message: 'Please input valid email!' }
                    ]}
                  >
                    <Input 
                      prefix={<MailOutlined />} 
                      placeholder="Email"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          {/* Change Password */}
          <Card title="Change Password" style={{ marginTop: 24 }}>
            <Form
              layout="vertical"
              onFinish={handleChangePassword}
            >
              <Form.Item
                name="current_password"
                label="Current Password"
                rules={[
                  { required: true, message: 'Please input current password!' }
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="Current password"
                />
              </Form.Item>

              <Form.Item
                name="new_password"
                label="New Password"
                rules={[
                  { required: true, message: 'Please input new password!' },
                  { min: 6, message: 'Password must be at least 6 characters!' }
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="New password"
                />
              </Form.Item>

              <Form.Item
                name="confirm_password"
                label="Confirm New Password"
                dependencies={['new_password']}
                rules={[
                  { required: true, message: 'Please confirm new password!' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('new_password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="Confirm new password"
                />
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={passwordLoading}
                  icon={<LockOutlined />}
                >
                  Change Password
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* User Statistics */}
        <Col xs={24} lg={8}>
          <Card title="Account Information">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <strong>Role:</strong>
                <Tag color={user.is_admin ? 'blue' : 'default'} style={{ marginLeft: 8 }}>
                  {user.is_admin ? 'Admin' : 'User'}
                </Tag>
              </div>
              
              <div>
                <strong>Status:</strong>
                <Tag color="green" style={{ marginLeft: 8 }}>
                  Active
                </Tag>
              </div>

              <div>
                <strong>Character Limit:</strong>
                <span style={{ marginLeft: 8, fontWeight: 'bold' }}>
                  {user.char_limit === 0 ? 'No plan' : (user.char_limit !== null && user.char_limit !== undefined && user.char_limit < 999999999 ? user.char_limit.toLocaleString() : 'Unlimited')}
                </span>
              </div>

              <div>
                <strong>Used Characters:</strong>
                <span style={{ marginLeft: 8, fontWeight: 'bold' }}>
                  {user.used_chars ? user.used_chars.toLocaleString() : '0'}
                </span>
              </div>

              {user.char_limit > 0 && (
                <div>
                  <strong>Remaining:</strong>
                  <span style={{ marginLeft: 8, fontWeight: 'bold', color: '#52c41a' }}>
                    {(user.char_limit - user.used_chars).toLocaleString()}
                  </span>
                </div>
              )}
            </Space>
          </Card>

          <Card title="Usage Statistics" style={{ marginTop: 24 }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Total Requests"
                  value={userStats.totalRequests}
                  prefix={<UserOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Completed"
                  value={userStats.completedRequests}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Pending"
                  value={userStats.pendingRequests}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Chars Used"
                  value={userStats.totalCharsUsed}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
            </Row>
          </Card>

        </Col>
      </Row>
    </div>
  );
}

export default Profile;